import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const services = [
  "Front-End Development",
  "WordPress",
  "LMS Development",
  "Performance & SEO",
  "E-Commerce Builds",
  "Website Redesign & Maintenance",
  "Content Creation"
];

const ServiceLine = ({ service, index, total, containerRef }) => {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const start = (index / total) * 0.7;
  const end = ((index + 1.5) / total) * 0.7;

  const opacity = useTransform(
    scrollYProgress,
    [start, start + 0.08, end - 0.05, end],
    [0, 1, 1, 0.06]
  );
  const y = useTransform(scrollYProgress, [start, start + 0.1], [50, 0]);
  const scale = useTransform(
    scrollYProgress,
    [start, start + 0.08, end - 0.05, end],
    [0.94, 1, 1, 0.97]
  );

  const fontSize =
    index === 0
      ? "text-[48px] md:text-[62px] lg:text-[90px]"
      : "text-[36px] md:text-[42px] lg:text-[70px]";

  return (
    <motion.p
      style={{ opacity, y, scale }}
      className={`${fontSize} font-black uppercase leading-[1.0] tracking-tight text-white text-center`}
    >
      {service}
    </motion.p>
  );
};

const Services = () => {
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });


  const spotOpacity = useTransform(scrollYProgress, [0, 0.1, 0.65, 0.75], [0, 1, 1, 0]);

  return (
    <section
      ref={sectionRef}
      className="relative py-[70px] md:py-[90px] mb-14 md:mb-20 text-center bg-[#171718] border-b-2 border-[#7C6AE6]"
      style={{ minHeight: `${services.length * 45 + 60}vh` }}
    >

      {/* ── STICKY SCROLL ZONE ── */}
      <div
        ref={stickyRef}
        style={{ height: `${services.length * 45}vh` }}
        className="relative"
      >
        <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">

          {/* Spotlight */}
          <motion.div
            style={{ opacity: spotOpacity }}
            className="absolute top-[-60px] left-1/2 -translate-x-1/2 w-[500px] h-[280px]
                        blur-[100px] rounded-full pointer-events-none"
          />

          <div className="max-w-[1300px] mx-auto px-6 w-full">
          {/* HEADING */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-14  md:mb-20 text-center"
        >
          <h2 className="flex justify-center items-center text-center   text-[32px]
              sm:text-[42px]
              md:text-[48px]
              lg:text-[50px]
              uppercase
              font-semibold
              leading-[0.95]
              text-white
              ">
My Services
          </h2>

          <p className="mt-3 flex items-center justify-center text-white/70 text-sm md:text-base">
           A range of services focused on building clear, modern, and high-performing digital experiences.
          </p>
        </motion.div>

            <div className="flex mb-[40px] flex-col items-center text-center gap-0">
              {services.map((service, i) => (
                <ServiceLine
                  key={service}
                  service={service}
                  index={i}
                  total={services.length}
                  containerRef={sectionRef}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── DESIGN PHILOSOPHY — fully below sticky zone, never overlaps ── */}
      <div className="relative z-10 bg-[#171718] px-6 pb-24 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="max-w-[1300px] mx-auto grid md:grid-cols-12 gap-8 items-start
                     border-t border-white/10 pt-14"
        >
          <div className="md:col-span-4 ">
            <span className="text-[11px] tracking-[0.25em] font-bold text-[#7C6AE6] uppercase">
              Design philosophy
            </span>
          </div>

          <div className="md:col-span-8 text-left ">
            <p className="text-[22px]  md:text-[28px] uppercase leading-tight font-semibold text-white">
              The details
              <span className="block text-white/25 font-medium">are not the details.</span>
              <span className="block mt-2">They make the product & design.</span>
            </p>
            <span className="mt-5 block text-sm tracking-wider text-[#7C6AE6]">
              — Charles Eames
            </span>
            <p className="mt-8 text-[14px] md:text-[17px] text-white/40 max-w-2xl leading-relaxed">
              That's why I pay close attention to how products look, feel, and behave —
              because small decisions shape the entire experience.
            </p>
          </div>
        </motion.div>
      </div>

    </section>
  );
};

export default Services;